import { useState } from "react";
import {
  User,
  Lock,
  Shield,
  Check,
  AlertCircle,
  Loader2,
  Pencil,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { authApi } from "@/api/client";

export default function Profile() {
  const { user, refreshUser } = useAuth();

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(user?.name || "");
  const [editEmail, setEditEmail] = useState(user?.email || "");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleStartEditing = () => {
    setEditName(user?.name || "");
    setEditEmail(user?.email || "");
    setIsEditingProfile(true);
    setProfileError(null);
    setProfileSuccess(false);
  };

  const handleCancelEditing = () => {
    setIsEditingProfile(false);
    setProfileError(null);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);

    if (!editName.trim()) {
      setProfileError("Name is required");
      return;
    }

    if (!editEmail.trim()) {
      setProfileError("Email is required");
      return;
    }

    setIsUpdatingProfile(true);

    try {
      await authApi.updateProfile(editName.trim(), editEmail.trim());
      setProfileSuccess(true);
      setIsEditingProfile(false);
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setProfileError(
        axiosError.response?.data?.error || "Failed to update profile",
      );
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    setIsChangingPassword(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setPasswordError(
        axiosError.response?.data?.error || "Failed to change password",
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getPrimaryRole = () => {
    const primaryRole = user?.roles?.find((r) => r.is_primary);
    return (
      primaryRole?.role_name ||
      user?.roles?.[0]?.role_name ||
      "No role assigned"
    );
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and password
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Account Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-gray-400" />
              Account Information
            </h2>
            {!isEditingProfile && (
              <button
                onClick={handleStartEditing}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>
          <div className="px-6 py-4 space-y-4">
            {profileError && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-2 text-sm text-red-700">{profileError}</p>
                </div>
              </div>
            )}

            {profileSuccess && (
              <div className="rounded-md bg-green-50 p-3">
                <div className="flex">
                  <Check className="h-5 w-5 text-green-400" />
                  <p className="ml-2 text-sm text-green-700">
                    Profile updated successfully
                  </p>
                </div>
              </div>
            )}

            {isEditingProfile ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label
                    htmlFor="edit-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="edit-email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="edit-email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={isUpdatingProfile}
                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingProfile ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEditing}
                    disabled={isUpdatingProfile}
                    className="flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{user?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Role
              </label>
              <p className="mt-1 text-sm text-gray-900">{getPrimaryRole()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Permissions
              </label>
              <div className="mt-1 flex flex-wrap gap-2">
                {user?.is_org_admin && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Shield className="h-3 w-3 mr-1" />
                    Administrator
                  </span>
                )}
                {user?.is_approver && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <Check className="h-3 w-3 mr-1" />
                    Approver
                  </span>
                )}
                {!user?.is_org_admin && !user?.is_approver && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Standard User
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Two-Factor Authentication
              </label>
              <p className="mt-1 text-sm">
                {user?.totp_enabled ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Enabled
                  </span>
                ) : (
                  <span className="text-gray-500">Not enabled</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Lock className="h-5 w-5 text-gray-400" />
              Change Password
            </h2>
          </div>
          <form onSubmit={handleChangePassword} className="px-6 py-4 space-y-4">
            {passwordError && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-2 text-sm text-red-700">{passwordError}</p>
                </div>
              </div>
            )}

            {passwordSuccess && (
              <div className="rounded-md bg-green-50 p-3">
                <div className="flex">
                  <Check className="h-5 w-5 text-green-400" />
                  <p className="ml-2 text-sm text-green-700">
                    Password changed successfully
                  </p>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="current-password"
                className="block text-sm font-medium text-gray-700"
              >
                Current Password
              </label>
              <input
                type="password"
                id="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isChangingPassword}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingPassword ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Change Password"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
